import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ChangePasswordModal = ({ onClose }) => {
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error(t('cambiar.noCoinciden'));
      return;
    }

    // MISMA REGLA QUE EL SERVIDOR (deps.validar_password_politica), Ann 2026-07-20:
    // 8+ caracteres, con al menos una letra y un número. Antes la pantalla pedía 6
    // y el servidor 8 → la clienta veía un error raro tras enviar.
    const np = formData.new_password || '';
    if (np.length < 8 || !/[A-Za-zÁÉÍÓÚáéíóúÑñ]/.test(np) || !/\d/.test(np)) {
      toast.error(t('cambiar.reglaPass'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: formData.old_password,
        new_password: formData.new_password
      });
      toast.success(t('cambiar.cambiadaOk'));
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('cambiar.errorCambiar'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-teal-500" />
            {t('cambiar.titulo')}
          </DialogTitle>
          <DialogDescription>
            {t('cambiar.sub')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="old_password">{t('cambiar.actual')}</Label>
            <Input
              id="old_password"
              data-testid="old-password-input"
              type="password"
              value={formData.old_password}
              onChange={(e) => setFormData({ ...formData, old_password: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password">{t('cambiar.nueva')}</Label>
            <Input
              id="new_password"
              data-testid="new-password-input"
              type="password"
              value={formData.new_password}
              onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
              required
              minLength={6}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">{t('cambiar.confirmar')}</Label>
            <Input
              id="confirm_password"
              data-testid="confirm-password-input"
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
              minLength={6}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              data-testid="cancel-change-password-button"
              variant="outline"
              onClick={onClose}
            >
              {t('comun.cancelar')}
            </Button>
            <Button
              type="submit"
              data-testid="save-password-button"
              className="bg-teal-500 hover:bg-teal-600"
              disabled={loading}
            >
              {loading ? t('cambiar.guardando') : t('cambiar.titulo')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
